import { genreOptionsAssessment } from "../../constants";
import classes from "./criteriaSubmission.module.css";
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectFormData,
  setFormData,
} from "../../slices/form";
import SpecialInstructions from "./assesmentForm/specialInstructions";
import UploadContent from "../UI/UploadContent/uploadContent";
import Genre from "./assesmentForm/genre";
import Subject from "./assesmentForm/subject";
import TypeOfTest from "./assesmentForm/typeOfTest";
import DifficultyLevel from "./assesmentForm/difficultyLevel";
import Grade from "./assesmentForm/grade";
import QuestionsTypes from "./assesmentForm/questionTypes";

const closedBookOptions = {
  knowledge: true,
  analysis: true,
  comprehension: true,
  application: true,
};

const openBookOptions = {
  theme: true,
  analysis: true,
  structure: true,
  tone: true,
  vocabulary: true,
};

const mathOptions = {
  fluency: true,
  concept: true,
  application: true,
  reasoning: true,
};

const placeholderTextTopics =
  "Example:\n\nTopics: Populism era, exploring its roots in the marginalized western farmers, its challenge to the established two-party system, and the parallels between key societal issues of the Progressive Era and today's concerns.\n\nText book: Give me Liberty by Eric Foner\n\nKey terms: Progressive Era. Populism. Homestead Act. Corporate abuses. Monopolies. Wealth gap. Women’s rights. Race discussions. Immigration. Granger Movement. People’s Party. William Jennings Bryan. Omaha Platform. Third-party system. Winner-takes-all voting. Duverger’s Law.\n\nA few extra notes:\n- Parallels with today's societal issues: Environmental concerns: then (conservationism) vs. now (climate change). Abuses by large corporations and company monopolies. Wealth gap disparities. Women’s rights: then (voting) vs. now (ERA, abortion). Racial debates: then (inherent inequality) vs. now (defining equality). Immigration debates.\n- Populism’s Inception: Initiated by marginalized western farmers. Self-organizing farmers led to the Populist Party. Challenged the dominant two-party system.\n- Populists aligned with southern farmers, black farmers (at least at first), Northern industrial workers, and women. To gain Southern white votes, they severed ties with black Populists.\n- Election: William Jennings Bryan as their representative. Lost due to limited funds and separation from black Populists.\n- Legacy: Persistence of many Populist ideas. Major parties fortified defenses against third parties. Highlighted detrimental impacts of racism.\n\n";
const placeholderTextTopics2 = "You can type or paste your content here. Alternatively, or in addition, you can also upload your files using the upload button below.";
const genrePlaceholders = {
  "Nonfiction text (eg: textbook chapter, primary source, etc)":
    placeholderTextTopics2,
  "Fictional text (eg: reading passage, book chapter, poem, etc)":
    placeholderTextTopics2,
  "List of terms (eg: lesson plan, terms, syllabus, notes, etc)":
    placeholderTextTopics,
};

function CreateAssessment({ isLoading }) {
  const formData = useSelector(selectFormData);
  const dispatch = useDispatch();

  const { questions, genre, subject } = formData || {};

  const getPlaceholderText = (genre) => {
    return genrePlaceholders[genre] || placeholderTextTopics2;
  };
  
  const handleInputChange = (event) => { 
    const { name } = event.target; 
    let inputValue = event.target.value; 
    let updates = { [name]: inputValue };

    if (name === "show_text") {
      updates = {
        ...updates,
        genre: genreOptionsAssessment[0],
      };
    }
    // For assessment forms, we no longer handle text field here since
    // UploadContent now manages readingPassage directly
    // Keep other form field handling unchanged

    dispatch(setFormData(updates));
  };

  // Text syncing removed - UploadContent now manages readingPassage directly


  useEffect(() => {
    let initialSkills = {};
    
    if (subject === "Math") {
      initialSkills = mathOptions;
    } else if (formData?.show_text === "Open book") {
      initialSkills = openBookOptions;
    } else {
      initialSkills = closedBookOptions;
    }

    dispatch(
      setFormData({
        questions: {
          ...questions,
          skills: initialSkills,
          question_scope_passage_only: !(formData?.show_text === "Open book"), // Safe optional chaining
        },
      })
    );
  }, [dispatch, subject, formData?.show_text]); // Ensure effect runs if subject or show_text changes

  return (
    <>
      <Subject isLoading={isLoading} selectStoreData={selectFormData} setStoreData={setFormData}/>
      <Grade isLoading={isLoading} selectStoreData={selectFormData} setStoreData={setFormData}/>
      <DifficultyLevel
        isLoading={isLoading}
        handleInputChange={handleInputChange}
      />
      <UploadContent 
        textLabel="Type/paste your content here, and/or upload a file below:"
        textPlaceholder={getPlaceholderText(genre)}
        nameInStore="readingPassage"
        selectStoreData={(state) => ({ 
          readingPassage: state.form?.readingPassage || "", 
          text_files: state.form?.formData?.text_files || [] 
        })}
        setStoreData={setFormData}
        useReadingPassage={true}
        />
      <TypeOfTest
        isLoading={isLoading}
        handleInputChange={handleInputChange}
      />
      { /* don't display genre if subject is math */ }
      {subject && subject.toLowerCase() !== 'math' && (
        <Genre isLoading={isLoading} handleInputChange={handleInputChange} />
      )}
      <QuestionsTypes />
      <SpecialInstructions />
    </>
  );
}

export default CreateAssessment;
